/**
 * Task #1976 — Custom AI (5° provider OpenAI-compatible tenant-scoped).
 *
 * Schemi Zod canonici per:
 *   - row DB (mirror tabelle `custom_llm_endpoints`, `custom_llm_models`,
 *     `custom_llm_endpoint_health`);
 *   - input CRUD (create/update endpoint, register model con capabilities);
 *   - risposta `GET /v1/models` per discovery automatica.
 *
 * Vincoli evergreen `base_url`:
 *   - schema https obbligatorio;
 *   - blocca localhost, 127.0.0.0/8, RFC1918 (10/8, 172.16/12, 192.168/16),
 *     link-local 169.254/16. La validazione lato DB è enforced da
 *     `custom_llm_endpoints_base_url_https_chk`; questo schema aggiunge il
 *     check sugli host privati che SQL non può esprimere in modo pulito.
 */
import { z } from 'zod';

export const CUSTOM_ENDPOINT_KINDS = [
  'ollama',
  'vllm',
  'runpod',
  'lmstudio',
  'together',
  'groq',
  'mistral',
  'openrouter',
  'openai_compatible',
] as const;

export type CustomEndpointKind = (typeof CUSTOM_ENDPOINT_KINDS)[number];

export const CustomEndpointKindSchema = z.enum(CUSTOM_ENDPOINT_KINDS);

export const CUSTOM_HEALTH_STATUSES = ['unknown', 'ok', 'degraded', 'down'] as const;
export type CustomHealthStatus = (typeof CUSTOM_HEALTH_STATUSES)[number];
export const CustomHealthStatusSchema = z.enum(CUSTOM_HEALTH_STATUSES);

// ----------------------------------------------------------------------------
// base_url validator: https obbligatorio + blocca localhost/RFC1918/link-local
// ----------------------------------------------------------------------------

// L'hostname arriva normalizzato da `new URL` (es. "0" → "0.0.0.0",
// "127.1" → "127.0.0.1", "[::ffff:127.0.0.1]" → "[::ffff:7f00:1]").
const PRIVATE_HOST_PATTERNS: ReadonlyArray<RegExp> = [
  /^localhost$/i,
  /^0\./,                // 0.0.0.0/8: su molti sistemi raggiunge il loopback
  /^127\./,
  /^10\./,
  /^192\.168\./,
  /^169\.254\./,
  /^172\.(1[6-9]|2[0-9]|3[0-1])\./,
  /^::1$/,
  /^\[?::1\]?$/,
  /^\[?::\]?$/,          // indirizzo IPv6 non specificato
  /^\[?::ffff:/i,        // IPv4 mappato in IPv6 (può puntare a host privati)
  /^\[?f[cd][0-9a-f]{2}:/i, // fc00::/7 (unique local IPv6: fc.. e fd..)
  /^\[?fe80:/i,          // link-local IPv6
];

export function isPrivateHost(hostname: string): boolean {
  const h = hostname.toLowerCase().trim();
  return PRIVATE_HOST_PATTERNS.some((re) => re.test(h));
}

export const BaseUrlSchema = z
  .string()
  .trim()
  .min(1, 'base_url richiesto')
  .max(2048, 'base_url troppo lungo')
  .superRefine((val, ctx) => {
    let parsed: URL;
    try {
      parsed = new URL(val);
    } catch {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'base_url non è una URL valida' });
      return;
    }
    if (parsed.protocol !== 'https:') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'base_url deve usare schema https://',
      });
    }
    if (isPrivateHost(parsed.hostname)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          'Host privato/loopback non consentito. Esponi il servizio via reverse proxy HTTPS pubblico (es. Nginx + Let’s Encrypt) e usa quel hostname.',
      });
    }
  });

// ----------------------------------------------------------------------------
// Capabilities
// ----------------------------------------------------------------------------

// Task #2015 — capabilities estese: embedding (probe `/v1/embeddings` reale)
// + audio_transcription/audio_speech (classificate heuristic dal model_id).
// Le nuove default a false: i provider OpenAI-compatible le dichiarano
// esplicitamente alla registrazione del modello, e il probe le conferma.
export const CustomLlmCapabilitiesSchema = z.object({
  chat: z.boolean().default(true),
  tools: z.boolean().default(false),
  json_mode: z.boolean().default(false),
  streaming: z.boolean().default(true),
  vision: z.boolean().default(false),
  embedding: z.boolean().default(false),
  audio_transcription: z.boolean().default(false),
  audio_speech: z.boolean().default(false),
  // Task #2015 — `rerank` esplicita: il catalog (`explodeCapabilities`) emette
  // una row capability='rerank' quando true. Default false: il provider la
  // dichiara alla registrazione del modello (rerank non è probable in modo
  // affidabile via HTTP, resta self-declared).
  rerank: z.boolean().default(false),
  // Task #2997 — `realtime_audio`: capability speech-to-speech full-duplex
  // (es. OpenAI Realtime / Gemini Live). RILEVABILE dai metadata dell'endpoint
  // (`output_modalities` include `audio` + canale realtime), ma NON cablata in
  // esecuzione in questo round (nessuno standard OpenAI-compatible per il
  // realtime WS — vedi Out of scope). Default false, fail-closed.
  realtime_audio: z.boolean().default(false),
});

export type CustomLlmCapabilities = z.infer<typeof CustomLlmCapabilitiesSchema>;

export const CUSTOM_LLM_CAPABILITY_KEYS = [
  'chat',
  'tools',
  'json_mode',
  'streaming',
  'vision',
  'embedding',
  'audio_transcription',
  'audio_speech',
  'rerank',
] as const;
export type CustomLlmCapabilityKey = (typeof CUSTOM_LLM_CAPABILITY_KEYS)[number];

// ----------------------------------------------------------------------------
// Row schemas (DB → API)
// ----------------------------------------------------------------------------

export const CustomLlmEndpointRowSchema = z.object({
  id: z.string().uuid(),
  tenant_id: z.string().uuid(),
  name: z.string().min(1).max(120),
  kind: CustomEndpointKindSchema,
  base_url: z.string(),
  api_key_hint: z.string(),
  default_headers: z.record(z.string()).default({}),
  include_in_routing: z.boolean(),
  request_timeout_ms: z.number().int().min(1000).max(120000),
  health_status: CustomHealthStatusSchema,
  last_probe_at: z.string().nullable(),
  consecutive_failures: z.number().int().nonnegative(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type CustomLlmEndpointRow = z.infer<typeof CustomLlmEndpointRowSchema>;

export const CustomLlmModelRowSchema = z.object({
  id: z.string().uuid(),
  tenant_id: z.string().uuid(),
  endpoint_id: z.string().uuid(),
  model_name: z.string().min(1).max(255),
  display_name: z.string().max(255).nullable(),
  capabilities: CustomLlmCapabilitiesSchema,
  context_window: z.number().int().positive().nullable(),
  default_for_capability: z.array(z.string()).default([]),
  // Prezzo OPZIONALE dichiarato dal tenant (EUR per 1k token). NULL =
  // sconosciuto → le chiamate su questo modello restano `pricing_unknown`,
  // mai costo zero silenzioso.
  price_in_eur_per_1k: z.number().nonnegative().finite().nullable(),
  price_out_eur_per_1k: z.number().nonnegative().finite().nullable(),
  // Falso quando l'endpoint non elenca più il modello da
  // MODEL_UNAVAILABLE_AFTER_MISSED_DISCOVERIES discovery riuscite: escluso dal
  // catalogo e dal routing finché non ricompare.
  available: z.boolean(),
  last_probe_at: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type CustomLlmModelRow = z.infer<typeof CustomLlmModelRowSchema>;

export const CustomLlmEndpointHealthRowSchema = z.object({
  id: z.string(),
  tenant_id: z.string().uuid(),
  endpoint_id: z.string().uuid(),
  probe_kind: z.enum(['connection', 'discovery', 'capability']),
  status: z.enum(['ok', 'degraded', 'down']),
  http_status: z.number().int().nullable(),
  latency_ms: z.number().int().nullable(),
  error_excerpt: z.string().nullable(),
  detail: z.unknown().nullable(),
  probed_at: z.string(),
});
export type CustomLlmEndpointHealthRow = z.infer<typeof CustomLlmEndpointHealthRowSchema>;

// ----------------------------------------------------------------------------
// Input schemas (API → BE)
// ----------------------------------------------------------------------------

export const CreateCustomEndpointInputSchema = z.object({
  name: z.string().trim().min(1).max(120),
  kind: CustomEndpointKindSchema,
  base_url: BaseUrlSchema,
  api_key: z.string().min(1, 'api_key obbligatoria').max(2048),
  default_headers: z.record(z.string()).optional(),
  include_in_routing: z.boolean().optional(),
  request_timeout_ms: z.number().int().min(1000).max(120000).optional(),
});
export type CreateCustomEndpointInput = z.infer<typeof CreateCustomEndpointInputSchema>;

export const UpdateCustomEndpointInputSchema = z.object({
  name: z.string().trim().min(1).max(120).optional(),
  base_url: BaseUrlSchema.optional(),
  api_key: z.string().min(1).max(2048).optional(),
  default_headers: z.record(z.string()).optional(),
  include_in_routing: z.boolean().optional(),
  request_timeout_ms: z.number().int().min(1000).max(120000).optional(),
});
export type UpdateCustomEndpointInput = z.infer<typeof UpdateCustomEndpointInputSchema>;

export const RegisterCustomModelInputSchema = z.object({
  model_name: z.string().trim().min(1).max(255),
  display_name: z.string().trim().max(255).optional(),
  capabilities: CustomLlmCapabilitiesSchema.partial().optional(),
  context_window: z.number().int().positive().optional(),
  default_for_capability: z.array(z.string()).optional(),
});
export type RegisterCustomModelInput = z.infer<typeof RegisterCustomModelInputSchema>;

export const UpdateCustomModelInputSchema = z.object({
  display_name: z.string().trim().max(255).nullable().optional(),
  capabilities: CustomLlmCapabilitiesSchema.partial().optional(),
  context_window: z.number().int().positive().nullable().optional(),
  default_for_capability: z.array(z.string()).optional(),
  // Prezzi EUR/1k opzionali: `null` esplicito li azzera (torna pricing_unknown).
  price_in_eur_per_1k: z.number().nonnegative().finite().nullable().optional(),
  price_out_eur_per_1k: z.number().nonnegative().finite().nullable().optional(),
});
export type UpdateCustomModelInput = z.infer<typeof UpdateCustomModelInputSchema>;

// ----------------------------------------------------------------------------
// OpenAI-compatible /v1/models response shape
// ----------------------------------------------------------------------------

export const OpenAiCompatibleModelSchema = z.object({
  id: z.string().min(1),
  object: z.string().optional(),
  created: z.number().optional(),
  owned_by: z.string().optional(),
});
export type OpenAiCompatibleModel = z.infer<typeof OpenAiCompatibleModelSchema>;

export const OpenAiCompatibleModelsResponseSchema = z.object({
  object: z.string().optional(),
  data: z.array(OpenAiCompatibleModelSchema),
});
export type OpenAiCompatibleModelsResponse = z.infer<typeof OpenAiCompatibleModelsResponseSchema>;

// ----------------------------------------------------------------------------
// Preset metadata (FE quick-setup cards)
// ----------------------------------------------------------------------------

export interface CustomEndpointPreset {
  kind: CustomEndpointKind;
  label: string;
  description: string;
  docsUrl: string;
  defaultBaseUrlHint: string;
}

export const CUSTOM_ENDPOINT_PRESETS: ReadonlyArray<CustomEndpointPreset> = [
  {
    kind: 'ollama',
    label: 'Ollama',
    description: 'Self-hosted runtime locale. Esponi via reverse proxy HTTPS.',
    docsUrl: 'https://ollama.com/blog/openai-compatibility',
    defaultBaseUrlHint: 'https://ollama.tuo-dominio.it/v1',
  },
  {
    kind: 'vllm',
    label: 'vLLM',
    description: 'Serving GPU OSS ad alte performance, OpenAI-compatible.',
    docsUrl: 'https://docs.vllm.ai/en/latest/serving/openai_compatible_server.html',
    defaultBaseUrlHint: 'https://vllm.tuo-dominio.it/v1',
  },
  {
    kind: 'runpod',
    label: 'RunPod (vLLM Worker)',
    description: 'GPU serverless via worker vLLM OpenAI-compatible.',
    docsUrl: 'https://docs.runpod.io/serverless/workers/vllm/openai-compatibility',
    defaultBaseUrlHint: 'https://api.runpod.ai/v2/<endpoint_id>/openai/v1',
  },
  {
    kind: 'lmstudio',
    label: 'LM Studio',
    description: 'Desktop runtime. Esponi via tunnel HTTPS pubblico.',
    docsUrl: 'https://lmstudio.ai/docs/local-server',
    defaultBaseUrlHint: 'https://lmstudio.tuo-dominio.it/v1',
  },
  {
    kind: 'together',
    label: 'Together AI',
    description: 'Managed OSS models (Llama, Mistral, Qwen) OpenAI-compatible.',
    docsUrl: 'https://docs.together.ai/docs/openai-api-compatibility',
    defaultBaseUrlHint: 'https://api.together.xyz/v1',
  },
  {
    kind: 'groq',
    label: 'Groq',
    description: 'LPU inference cloud (Llama 3, Mixtral) low-latency.',
    docsUrl: 'https://console.groq.com/docs/openai',
    defaultBaseUrlHint: 'https://api.groq.com/openai/v1',
  },
  {
    kind: 'mistral',
    label: 'Mistral AI',
    description: 'API ufficiale Mistral (subset OpenAI-compatible).',
    docsUrl: 'https://docs.mistral.ai/api/',
    defaultBaseUrlHint: 'https://api.mistral.ai/v1',
  },
  {
    kind: 'openrouter',
    label: 'OpenRouter',
    description: 'Gateway con una sola chiave verso modelli di più fornitori (OpenAI, Anthropic, Google, Meta, Mistral…). Capability lette dal listing modelli.',
    docsUrl: 'https://openrouter.ai/docs/quickstart',
    defaultBaseUrlHint: 'https://openrouter.ai/api/v1',
  },
  {
    kind: 'openai_compatible',
    label: 'Custom OpenAI-compatible',
    description: 'Qualunque endpoint che espone /v1/chat/completions.',
    docsUrl: 'https://platform.openai.com/docs/api-reference/chat',
    defaultBaseUrlHint: 'https://api.tuo-provider.com/v1',
  },
];
